// API client + live-polling hook for the control-plane dashboard.
//
// ALL /api requests (reads and writes) require auth — every call attaches the
// Entra Bearer token (when required) and/or the dev X-Api-Key. See docs/api/contract.md.

import { useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { http } from "./http";
import {
  ApiProvider,
  FileChange,
  GitHubAppStatus,
  ModelCatalogEntry,
  Provider,
  Run,
  StageConfig,
  StageLog,
  StageMetric,
  SystemReview,
  WorkOrder,
} from "./types";

import { getApiUrl, getEntraClientId } from "../utils/runtimeConfig";
export const API_URL = getApiUrl();
// OIDC scopes — produces a Graph-audience token (aud=graph.microsoft.com).
// The API's ValidAudiences config accepts this. Switch to api://{clientId}/.default
// once "Expose an API" is configured in the Azure app registration.
const API_SCOPES = ["openid", "profile", "email"];

export interface RunListQuery {
  page?: number;
  pageSize?: number;
  status?: string;
  storySlug?: string;
  search?: string;
  sort?: "createdAt" | "title" | "storySlug" | "status" | "iterations" | "completedAt";
  order?: "asc" | "desc";
  limit?: number;
}

export interface PagedRuns {
  runs: Run[];
  total: number;
  page: number;
  pageSize: number;
}

export interface MetricQuery {
  runId?: string;
  stage?: string;
  model?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export interface LogQuery {
  runId?: string;
  stage?: string;
  from?: string;
  to?: string;
  limit?: number;
}

// Drops undefined/empty values so they aren't sent as `?stage=` etc.
function clean(params: MetricQuery | LogQuery) {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = String(v);
  }
  return out;
}

export interface ApiClient {
  getRuns(limit?: number): Promise<Run[]>;
  getRunsPaged(q: RunListQuery): Promise<PagedRuns>;
  getRun(id: string): Promise<Run>;
  getMetrics(q?: MetricQuery): Promise<StageMetric[]>;
  getLogs(q?: LogQuery): Promise<StageLog[]>;
  getConfig(): Promise<StageConfig[]>;
  getModels(): Promise<ModelCatalogEntry[]>;
  setStageModel(stage: string, model: string): Promise<StageConfig>;
  createModel(body: {
    name: string;
    provider: Provider;
    apiProvider?: ApiProvider | null;
    baseUrl?: string | null;
    costPerMillionTokensIn?: number;
    costPerMillionTokensOut?: number;
    notes?: string | null;
  }): Promise<ModelCatalogEntry>;
  updateModel(
    id: string,
    body: Partial<{
      name: string;
      provider: Provider;
      apiProvider: ApiProvider | null;
      baseUrl: string | null;
      costPerMillionTokensIn: number;
      costPerMillionTokensOut: number;
      notes: string | null;
    }>
  ): Promise<ModelCatalogEntry>;
  deleteModel(id: string): Promise<void>;
  createSystemReview(body: {
    runId?: string;
    stage?: string;
    model?: string;
    from?: string;
    to?: string;
    reviewModel?: string;
  }): Promise<SystemReview>;
  getSystemReviews(): Promise<SystemReview[]>;
  getWorkOrders(): Promise<WorkOrder[]>;
  approveWorkOrder(
    id: string,
    body?: { note?: string; runPipeline?: boolean }
  ): Promise<WorkOrder>;
  rejectWorkOrder(id: string, note?: string): Promise<WorkOrder>;
  openRunPr(id: string, body?: { title?: string; body?: string }): Promise<Run>;
  getChangeSet(id: string): Promise<FileChange[]>;
  getGitHubStatus(): Promise<GitHubAppStatus>;
}

export function useApi(): ApiClient {
  const { instance, accounts } = useMsal();
  const account = accounts[0] || instance.getActiveAccount();
  const isAuthenticated = !!account;

  // Stable ref for token acquisition (MSAL acquireTokenSilent).
  const authRef = useRef({ instance, account, isAuthenticated });
  authRef.current = { instance, account, isAuthenticated };

  // requireToken: when true, throw (and redirect interactively) if token acquisition fails.
  // When false (default), best-effort: attach token if available, continue without on failure.
  // Either way, always tries to attach Bearer when the user is signed in.
  const authHeaders = useCallback(
    async (opts?: { requireToken?: boolean }): Promise<Record<string, string>> => {
      const { instance, account, isAuthenticated } = authRef.current;
      const headers: Record<string, string> = {};
      const devKey = process.env.REACT_APP_API_KEY;
      if (devKey) headers["X-Api-Key"] = devKey;
      if (!isAuthenticated || !account) {
        if (opts?.requireToken) {
          if (process.env.REACT_APP_AUTH_DISABLED === "true") return headers;
          throw new Error("not signed in");
        }
        return headers;
      }
      // Identify actor for domain/audit (Entra account carries username/claims)
      const claims = account.idTokenClaims as Record<string, string> | undefined;
      const email = account.username || claims?.["preferred_username"] || claims?.["email"];
      if (email) headers["X-Actor-Email"] = email;

      // Always attach Bearer when signed in — reads and writes both require auth in production.
      try {
        const response = await instance.acquireTokenSilent({
          scopes: API_SCOPES,
          account,
        });
        headers.Authorization = `Bearer ${response.idToken}`;
      } catch (e) {
        if (opts?.requireToken) {
          // Fall back to interactive if silent fails
          await instance.acquireTokenRedirect({
            scopes: API_SCOPES,
            account,
          });
          throw new Error("Entra token acquisition redirected");
        }
        // For non-required calls: continue without token; server will 401 if no API key fallback.
      }
      return headers;
    },
    [instance]
  );

  const getRuns = useCallback(
    async (limit?: number) => {
      const params: Record<string, string> = {};
      if (limit) params.limit = String(limit);
      const data = await http.get<{ runs: Run[] }>(`${API_URL}/api/runs`, {
        params,
        headers: await authHeaders(),
      });
      return data.runs;
    },
    [authHeaders]
  );

  const getRunsPaged = useCallback(
    async (q: RunListQuery) => {
      const params: Record<string, string> = {};
      if (q.page) params.page = String(q.page);
      if (q.pageSize) params.pageSize = String(q.pageSize);
      if (q.status) params.status = q.status;
      if (q.storySlug) params.storySlug = q.storySlug;
      if (q.search) params.search = q.search;
      if (q.sort) params.sort = q.sort;
      if (q.order) params.order = q.order;
      return http.get<PagedRuns>(`${API_URL}/api/runs`, {
        params,
        headers: await authHeaders(),
      });
    },
    [authHeaders]
  );

  const getRun = useCallback(
    async (id: string) =>
      http.get<Run>(`${API_URL}/api/runs/${id}`, {
        headers: await authHeaders(),
      }),
    [authHeaders]
  );

  const getMetrics = useCallback(
    async (q: MetricQuery = {}) => {
      const data = await http.get<{ metrics: StageMetric[] }>(
        `${API_URL}/api/metrics`,
        { params: clean(q), headers: await authHeaders() }
      );
      return data.metrics;
    },
    [authHeaders]
  );

  const getLogs = useCallback(
    async (q: LogQuery = {}) => {
      const data = await http.get<{ logs: StageLog[] }>(`${API_URL}/api/logs`, {
        params: clean(q),
        headers: await authHeaders(),
      });
      return data.logs;
    },
    [authHeaders]
  );

  const getConfig = useCallback(async () => {
    const data = await http.get<{ stages: StageConfig[] }>(
      `${API_URL}/api/config`,
      { headers: await authHeaders() }
    );
    return data.stages;
  }, [authHeaders]);

  const getModels = useCallback(async () => {
    const data = await http.get<{ models: ModelCatalogEntry[] }>(
      `${API_URL}/api/models`,
      { headers: await authHeaders() }
    );
    return data.models;
  }, [authHeaders]);

  const setStageModel = useCallback(
    async (stage: string, model: string) => {
      const headers = await authHeaders();
      return http.put<StageConfig>(
        `${API_URL}/api/config/${stage}`,
        { model },
        { headers }
      );
    },
    [authHeaders]
  );

  const createModel = useCallback<ApiClient["createModel"]>(
    async (body) => {
      const headers = await authHeaders();
      return http.post<ModelCatalogEntry>(`${API_URL}/api/models`, body, {
        headers,
      });
    },
    [authHeaders]
  );

  const updateModel = useCallback<ApiClient["updateModel"]>(
    async (id, body) => {
      const headers = await authHeaders();
      return http.put<ModelCatalogEntry>(
        `${API_URL}/api/models/${id}`,
        body,
        { headers }
      );
    },
    [authHeaders]
  );

  const deleteModel = useCallback(
    async (id: string) => {
      const headers = await authHeaders();
      await http.delete(`${API_URL}/api/models/${id}`, { headers });
    },
    [authHeaders]
  );

  const createSystemReview = useCallback(
    async (body: {
      runId?: string;
      stage?: string;
      model?: string;
      from?: string;
      to?: string;
      reviewModel?: string;
    }) => {
      const headers = await authHeaders();
      return http.post<SystemReview>(
        `${API_URL}/api/system-reviews`,
        body,
        { headers }
      );
    },
    [authHeaders]
  );

  const getSystemReviews = useCallback(async () => {
    const data = await http.get<{ reviews: SystemReview[] }>(
      `${API_URL}/api/system-reviews`,
      { headers: await authHeaders() }
    );
    return data.reviews;
  }, [authHeaders]);

  const getWorkOrders = useCallback(async () => {
    const data = await http.get<{ workOrders: WorkOrder[] }>(
      `${API_URL}/api/work-orders`,
      { headers: await authHeaders() }
    );
    return data.workOrders;
  }, [authHeaders]);

  const approveWorkOrder = useCallback<ApiClient["approveWorkOrder"]>(
    async (id, body) => {
      // Human-only (JWT-gated): require a real Entra token, surfacing the reason
      // if it can't be obtained rather than falling back to the machine key.
      const headers = await authHeaders({ requireToken: true });
      return http.post<WorkOrder>(
        `${API_URL}/api/work-orders/${id}/approve`,
        body ?? {},
        { headers }
      );
    },
    [authHeaders]
  );

  const rejectWorkOrder = useCallback(
    async (id: string, note?: string) => {
      const headers = await authHeaders({ requireToken: true });
      return http.post<WorkOrder>(
        `${API_URL}/api/work-orders/${id}/reject`,
        { note },
        { headers }
      );
    },
    [authHeaders]
  );

  const openRunPr = useCallback<ApiClient["openRunPr"]>(
    async (id, body) => {
      // Human-only (JWT-gated): the GitHub App opens the PR server-side in code.
      const headers = await authHeaders({ requireToken: true });
      return http.post<Run>(`${API_URL}/api/runs/${id}/pr`, body ?? {}, { headers });
    },
    [authHeaders]
  );

  const getGitHubStatus = useCallback<ApiClient["getGitHubStatus"]>(
    async () =>
      http.get<GitHubAppStatus>(`${API_URL}/api/github/status`, {
        headers: await authHeaders(),
      }),
    [authHeaders]
  );

  const getChangeSet = useCallback<ApiClient["getChangeSet"]>(
    async (id) =>
      http.get<FileChange[]>(`${API_URL}/api/runs/${id}/changeset`, {
        headers: await authHeaders(),
      }),
    [authHeaders]
  );

  return useMemo<ApiClient>(
    () => ({
      getRuns,
      getRunsPaged,
      getRun,
      getMetrics,
      getLogs,
      getConfig,
      getModels,
      setStageModel,
      createModel,
      updateModel,
      deleteModel,
      createSystemReview,
      getSystemReviews,
      getWorkOrders,
      approveWorkOrder,
      rejectWorkOrder,
      openRunPr,
      getChangeSet,
      getGitHubStatus,
    }),
    [
      getRuns,
      getRunsPaged,
      getRun,
      getMetrics,
      getLogs,
      getConfig,
      getModels,
      setStageModel,
      createModel,
      updateModel,
      deleteModel,
      createSystemReview,
      getSystemReviews,
      getWorkOrders,
      approveWorkOrder,
      rejectWorkOrder,
      openRunPr,
      getChangeSet,
      getGitHubStatus,
    ]
  );
}

export interface LiveData<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
}

// Default poll cadence: slow on purpose. Live views refresh in the background
// infrequently; users hit the header Refresh button (refreshAllLiveData) for an
// immediate update, and a refetch also fires when the tab regains focus.
export const POLL_MS = 60000;

// Registry of mounted useLiveData refreshers so a single control (the header
// Refresh button) can refresh every live view at once.
const liveRefreshers = new Set<() => void>();
export function refreshAllLiveData(): void {
  liveRefreshers.forEach((fn) => fn());
}

// Polls `fetcher` every `intervalMs`, pausing while the tab is hidden and
// firing an immediate refresh when it becomes visible again. Re-subscribes only
// when `deps` change; the latest fetcher closure is always used via a ref.
// Skips overlapping in-flight requests so a slow response cannot stack polls.
export function useLiveData<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList,
  intervalMs = POLL_MS
): LiveData<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const tickRef = useRef<() => void>(() => {});

  useEffect(() => {
    let active = true;
    // inFlight is LOCAL to this subscription (not a shared ref): a fresh mount /
    // re-subscribe must always fetch immediately, even if a prior effect's fetch
    // is still settling. Otherwise the initial fetch would be skipped and pushed
    // out by a full interval (a long blank at a slow poll cadence).
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout>;

    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(run, intervalMs);
    };
    const run = async () => {
      if (!active) return;
      if (typeof document !== "undefined" && document.hidden) {
        schedule();
        return;
      }
      // A request is already running; it will schedule the next poll when it
      // finishes — don't stack or defer the data we're already fetching.
      if (inFlight) return;
      inFlight = true;
      try {
        const d = await fetcherRef.current();
        if (active) {
          setData(d);
          setError(null);
        }
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : "request failed");
      } finally {
        inFlight = false;
        if (active) {
          setLoading(false);
          schedule();
        }
      }
    };
    tickRef.current = () => {
      clearTimeout(timer);
      void run();
    };
    const onVisible = () => {
      if (!document.hidden) tickRef.current();
    };

    const refresher = () => tickRef.current();
    liveRefreshers.add(refresher);

    void run();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active = false;
      clearTimeout(timer);
      liveRefreshers.delete(refresher);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, intervalMs]);

  return { data, error, loading, refresh: () => tickRef.current() };
}